import { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  useSensors,
  useSensor,
  DragEndEvent,
  DragOverEvent,
  pointerWithin,
  DragOverlay,
  DragStartEvent,
} from '@dnd-kit/core';
import { DroppableZone } from './components/DroppableZone';
import { SortableCategory } from './components/SortableCategory';

import ConfirmationDialog from '../ConfirmationDialog';
import BreadCrumbComponent from './components/BreadCrumbComponent';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import {
  getMasterView,
  getFeatureFamilyList,
  showAddCategoryDialog,
  updateMasterView,
  saveMaster,
  updateCategoryModify,
} from '../../store/states/CategorySlice';
import { AppDispatch, AppState } from '../../store/AppStore';
import {
  EDropCategory,
  EConfirmDialog,
  EFeatureFamilyType,
  EActionType,
  ECategoryType,
  ECategoryErrorType,
} from '../../data/Constants';
import AddCategoryComponent from './components/AddCategoryComponent';
import { useTranslation } from 'react-i18next';
import {
  deepCloneSections,
  determineTargetZone,
  findAndRemoveNode,
  findSectionById,
  findSectionInTree,
  getUpdatedCategoryData,
  isInvalidCategoryDrop,
  isTargetMultiNumeric,
  isValidItemDrop,
  showCategoryError,
} from '../../services/CategoryHelper';
import SplitBar from './SplitBar';
import IconType from '../../components/InputComponents/IconType';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';

import type { ISection, IContent, IFeatureFamily, IBreadcrumbSection } from '../../../types';

/**
 * Category component that handles the display and manipulation of feature categories
 * Provides drag and drop functionality for organizing features into categories
 * @returns {JSX.Element} The rendered Category component
 */
const Category: React.FC = () => {
  const initDialog = {
    open: false,
    title: '',
    description: '',
    alertFor: '',
  };
  const { t } = useTranslation();

  const dispatch = useDispatch<AppDispatch>();
  const categoryState = useSelector((state: AppState) => state.category);
  const product = useSelector((state: AppState) => state.procos.product);

  const [sections, setSections] = useState<ISection[]>([]);
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [globalUnassignedItems, setGlobalUnassignedItems] = useState<IFeatureFamily[]>([]);
  const [isValidDragItem, setIsValidDragItem] = useState<boolean>(true);
  const [breadCrumb, setBreadCrumb] = useState<IBreadcrumbSection>();
  const [confirmDelete, setConfirmDelete] = useState(initDialog);
  const [sectionToDelete, setSectionToDelete] = useState<ISection | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [active, setActive] = useState<any>(null);
  const [overId, setOverId] = useState<any>(null);
  const [expanded, setExpanded] = useState<string[]>([]);
  const [showForbidden, setShowForbidden] = useState(false);
  const [editDataView, setEditDataView] = useState(false);

  // Initial data fetch
  const fetchData = async () => {
    if (!product?.id) return;

    const masterPayload = { productCode: product.id };
    const featurePayload = {
      productCode: product.id,
      includeScopeFeatures: false,
      workItemNum: '',
      page: 0,
      limit: 0,
    };

    // Execute both API calls in parallel for better performance
    await Promise.allSettled([
      dispatch(getMasterView(masterPayload)),
      dispatch(getFeatureFamilyList(featurePayload)),
    ]);
  };

  useEffect(() => {
    fetchData();
  }, [product?.id]);

  // Update local state when Redux state changes
  useEffect(() => {
    if (!categoryState?.masterView?.sections) return;

    const rootSections = deepCloneSections(categoryState.masterView.sections) || [];
    if (rootSections.length > 0 && !selectedSectionId) {
      setSelectedSectionId(rootSections[0].id);
    }
    calculateUnAssigned(rootSections);
  }, [categoryState.masterView?.sections, categoryState.featureFamily]);

  // Generate breadcrumb path
  useEffect(() => {
    if (!selectedSectionId) return;

    // Recursively find the path to the selected section
    function findPath(
      sections: ISection[],
      targetId: string,
      path: ISection[] = []
    ): ISection[] | null {
      for (const section of sections) {
        const nextPath = [...path, section];
        if (section.id === targetId) return nextPath;
        if (section.sections?.length) {
          const found = findPath(section.sections, targetId, nextPath);
          if (found) return found;
        }
      }
      return null;
    }

    const breadcrumbPath = findPath(sections, selectedSectionId);

    if (breadcrumbPath?.length) {
      // Mark the last section as active
      const pathWithActive = breadcrumbPath.map((section, idx) => ({
        ...section,
        active: idx === breadcrumbPath.length - 1,
      }));

      // Get the last item with a non-null assertion
      const lastItem = pathWithActive.at(-1)!;

      // Set breadcrumb with properly typed object
      setBreadCrumb({
        ...lastItem,
        breadcrumb: pathWithActive,
      });
    }
  }, [selectedSectionId, sections]);

  /**
   * Navigates to a specific section when clicking on a breadcrumb item
   * @param {ISection} section - The section to navigate to
   */
  const navigateToCategory = (section: ISection) => {
    setSelectedSectionId(section.id);
  };

  //split bar props
  const paneRefs = useRef<HTMLDivElement[]>([]);

  const resizeHandler = (newWidths: number[]) => {
    if (paneRefs.current && paneRefs.current.length === newWidths.length) {
      const totalWidth = newWidths.reduce((sum, width) => sum + width, 0);
      for (let index = 0; index < paneRefs.current.length; index++) {
        const pane = paneRefs.current[index];
        if (pane) {
          const percentageWidth = (newWidths[index] / totalWidth) * 100;
          pane.style.setProperty('--pane-width', `${percentageWidth}%`);
        }
      }
    }
  };

  const setPaneRef = (index: number) => (el: HTMLDivElement | null) => {
    if (el) {
      paneRefs.current[index] = el;
    }
  };

  /**
   * Validates if the dragged item is compatible with the target section
   * For MultiNumeric categories, only Numeric items are allowed
   * @param {any} over - The target drop area information
   * @returns {boolean} Whether the item can be dropped in the target
   */
  const isValidNumericItem = (over: any): boolean => {
    const itemType = active?.data?.current?.itemType;
    const isTargetCategory = over?.id?.startsWith(`${EDropCategory.Category}-`);
    const sectionType = selectedSection?.type;

    return isValidItemDrop(sectionType, itemType, isTargetCategory);
  };

  /**
   * Handles the start of a drag operation
   * Stores the active item information for use during dragging
   * @param {DragStartEvent} event - The drag start event
   */
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setActiveId(active.id as string);
    setActive(active);
  };

  // Get the currently selected section
  const selectedSection = useMemo(() => {
    if (!selectedSectionId) return null;
    return findSectionById(sections, selectedSectionId);
  }, [sections, selectedSectionId]);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 100,
        tolerance: 8,
      },
    })
  );

  /**
   * Handles drag over events to determine if the current drop target is valid
   * Updates visual feedback for the user during dragging
   * @param {DragOverEvent} event - The drag over event
   */
  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event;

    if (!over) return;

    let overId = null;
    // If hovering over an item in the assigned section
    overId = over.data?.current?.container;

    // Check forbidden for category drag
    if (
      active?.data?.current?.type === EDropCategory.Category &&
      over.data?.current?.type === EDropCategory.Category
    ) {
      const draggedId = active.data.current.sectionId;
      const targetId = over.data.current.sectionId;
      const draggedNode = findSectionById(sections, draggedId);
      const targetNode = findSectionById(sections, targetId);
      if (
        draggedNode &&
        targetNode &&
        isInvalidCategoryDrop(sections, draggedNode, targetNode, false, dispatch, t)
      ) {
        setShowForbidden(true);
      } else {
        setShowForbidden(false);
      }
    } else {
      setShowForbidden(false);
    }

    // Only validate if dragging an assigned/unassigned item
    const isDraggingItem =
      active?.data?.current?.container === EDropCategory.Assigned ||
      active?.data?.current?.container === EDropCategory.Unassigned;

    if (isDraggingItem) {
      if (overId) {
        setOverId(overId);
      }
      // If dragging to a MultiNumeric section or category, check if item is Numeric
      if (isTargetMultiNumeric(over)) {
        const isValid = isValidNumericItem(over);
        setIsValidDragItem(isValid);
      } else {
        setIsValidDragItem(true);
      }
    } else {
      // For non-MultiNumeric sections, always valid
      setIsValidDragItem(true);
    }
  };

  /**
   * Handles the end of a drag operation to complete the item drop
   * Processes the drop action based on the source and target zones
   * @param {DragEndEvent} event - The drag end event
   */
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    setActive(null);
    setOverId(null);

    if (!over) return;
    const itemId = active.id as string;
    const fromZone = active.data?.current?.container as string;
    const toZone = determineTargetZone(over);

    // Check invalid drops
    if (fromZone === EDropCategory.Assigned && toZone === EDropCategory.Unassigned) {
      showCategoryError(dispatch, t, ECategoryErrorType.INVALID_DRAG_DROP);
      return;
    }

    // Handle category to category drag
    if (
      active.data?.current?.type === EDropCategory.Category &&
      over.data?.current?.type === EDropCategory.Category
    ) {
      if (itemId !== over.data?.current?.sectionId) {
        // Move computation outside setState
        const updatedSections = moveCategoryIntoCategory(
          sections,
          itemId.replace(`${EDropCategory.Category}-`, ''),
          over.data?.current?.sectionId
        );

        // Only update state, no computation
        setSections(updatedSections);
        dispatch(updateCategoryModify());
        return;
      }
    }

    // Show error for invalid drops on MultiNumeric
    if (isTargetMultiNumeric(over) && !isValidNumericItem(over)) {
      showCategoryError(dispatch, t, ECategoryErrorType.MULTI_NUMERIC_WARNING);
      setIsValidDragItem(true);
      return;
    }

    // Determine target and update sections
    let updatedSections;
    if (toZone?.startsWith(`${EDropCategory.Category}-`)) {
      updatedSections = handleDragToCategory(sections, selectedSection, toZone, fromZone, itemId);
    } else {
      updatedSections = handleDragBetweenZones(
        sections,
        selectedSection,
        fromZone,
        toZone,
        itemId,
        over
      );
    }

    // Only use setSections for state update
    setSections(updatedSections);
    dispatch(updateCategoryModify());
  };

  const findParent = (
    sections: ISection[],
    childId: string,
    parent: ISection | null = null
  ): ISection | null => {
    for (const section of sections) {
      if (section.id === childId) return parent;
      if (section.sections) {
        const found = findParent(section.sections, childId, section);
        if (found) return found;
      }
    }
    return null;
  };

  /**
   * Moves a category (and its subtree) into another category
   * Enforces rules:
   * 1. Cannot drop a MultiNumeric category under any MultiNumeric category.
   * 2. Cannot drop a category onto itself (same name).
   * 3. Cannot drop any category under a MultiNumeric category.
   * 4. Non-numeric category/subcategory can only be dropped to non-numeric top-level category
   * @param {ISection[]} sections - The current sections tree
   * @param {string} draggedId - The id of the category being dragged
   * @param {string} targetId - The id of the category to drop into
   * @returns {ISection[]} The updated sections tree
   */
  const moveCategoryIntoCategory = (
    sections: ISection[],
    draggedId: string,
    targetId: string
  ): ISection[] => {
    const { newSections, draggedNode, targetNode } = findAndRemoveNode(
      sections,
      draggedId,
      targetId
    );

    if (!draggedNode || !targetNode) return sections;

    if (isInvalidCategoryDrop(sections, draggedNode, targetNode, true, dispatch, t)) {
      return sections;
    }

    if (!targetNode.sections) targetNode.sections = [];
    draggedNode.hideGeneral = true;
    targetNode.sections.push(draggedNode);

    return newSections;
  };

  /**
   * Handles dragging items to category sections
   * Determines whether to use MultiNumeric or regular category handling based on target type
   *
   * @param {ISection[]} sections - All sections in the current state
   * @param {ISection | null} currentSection - The currently selected section
   * @param {string} toZone - The target zone identifier
   * @param {string} fromZone - The source zone identifier
   * @param {string} itemId - The ID of the item being dragged
   * @returns {ISection[]} Updated sections with the item moved to the target category
   */
  const handleDragToCategory = (
    sections: ISection[],
    currentSection: ISection | null,
    toZone: string,
    fromZone: string,
    itemId: string
  ): ISection[] => {
    const targetSectionId = toZone.replace(`${EDropCategory.Category}-`, '');
    const targetSection = findSectionById(sections, targetSectionId);

    if (!targetSection) return sections;

    const updatedTargetSection = targetSection;
    const updatedCurrentSection = currentSection;

    if (updatedTargetSection.type === ECategoryType.MultiNumeric) {
      return handleDragToMultiNumericCategory(
        sections,
        updatedTargetSection,
        updatedCurrentSection,
        fromZone,
        itemId
      );
    } else {
      return handleDragToRegularCategory(
        sections,
        updatedTargetSection,
        updatedCurrentSection,
        fromZone,
        itemId
      );
    }
  };

  /**
   * Generic handler for dragging items to a category (MultiNumeric or Regular)
   * Handles type checks and content movement logic
   */
  const handleDragToCategoryGeneric = (
    sections: ISection[],
    targetSection: ISection,
    currentSection: ISection | null,
    fromZone: string,
    itemId: string,
    isMultiNumeric: boolean
  ): ISection[] => {
    const newTargetSection = findSectionInTree(sections, targetSection.id);
    const newCurrentSection = currentSection
      ? findSectionInTree(sections, currentSection.id)
      : null;

    if (fromZone === EDropCategory.Unassigned) {
      const itemToMove = globalUnassignedItems.find((item) => item.code === itemId);
      if (
        itemToMove &&
        (!isMultiNumeric || itemToMove.familyType === EFeatureFamilyType.Numeric) &&
        newTargetSection
      ) {
        const content: IContent = {
          code: itemToMove.code,
          type: itemToMove.familyType,
        };
        newTargetSection.contents = [...(newTargetSection.contents || []), content];
        setGlobalUnassignedItems((prev) => prev.filter((item) => item.code !== itemId));
      }
    } else if (fromZone === EDropCategory.Assigned && newCurrentSection && newTargetSection) {
      const itemToMove = newCurrentSection.contents.find((item) => item.code === itemId);
      if (itemToMove && (!isMultiNumeric || itemToMove.type === EFeatureFamilyType.Numeric)) {
        newCurrentSection.contents = newCurrentSection.contents.filter(
          (item) => item.code !== itemId
        );
        newTargetSection.contents = [...(newTargetSection.contents || []), { ...itemToMove }];
      }
    }
    return sections;
  };

  const handleDragToMultiNumericCategory = (
    sections: ISection[],
    targetSection: ISection,
    currentSection: ISection | null,
    fromZone: string,
    itemId: string
  ): ISection[] =>
    handleDragToCategoryGeneric(sections, targetSection, currentSection, fromZone, itemId, true);

  const handleDragToRegularCategory = (
    sections: ISection[],
    targetSection: ISection,
    currentSection: ISection | null,
    fromZone: string,
    itemId: string
  ): ISection[] =>
    handleDragToCategoryGeneric(sections, targetSection, currentSection, fromZone, itemId, false);

  /**
   * Handles dragging items between assigned and unassigned zones
   * Also handles reordering items within the same zone
   *
   * @param {ISection[]} sections - All sections in the current state
   * @param {ISection | null} currentSection - The currently selected section
   * @param {string} fromZone - The source zone identifier
   * @param {string} toZone - The target zone identifier
   * @param {string} itemId - The ID of the item being dragged
   * @param {any} over - Information about what the item is being dragged over
   * @returns {ISection[]} Updated sections with the item moved or reordered
   */
  const handleDragBetweenZones = (
    sections: ISection[],
    currentSection: ISection | null,
    fromZone: string,
    toZone: string,
    itemId: string,
    over: any
  ): ISection[] => {
    if (!currentSection) return sections;
    // Find the current section
    const newCurrentSection = findSectionInTree(sections, currentSection.id);

    if (!newCurrentSection) return sections;

    if (fromZone === EDropCategory.Unassigned && toZone === EDropCategory.Assigned) {
      const itemToMove = globalUnassignedItems.find((item) => item.code === itemId);
      if (itemToMove) {
        // For MultiNumeric sections, only allow Numeric features
        if (
          newCurrentSection.type === ECategoryType.MultiNumeric &&
          itemToMove.familyType !== EFeatureFamilyType.Numeric
        ) {
          // Invalid drag, don't allow the drop
          return sections;
        }

        const content: IContent = {
          code: itemToMove.code,
          type: itemToMove.familyType,
        };
        newCurrentSection.contents = [...(newCurrentSection.contents || []), content];
        setGlobalUnassignedItems((prev) => prev.filter((item) => item.code !== itemId));
      }
    } else if (fromZone === EDropCategory.Assigned && toZone === EDropCategory.Assigned) {
      const oldIndex = newCurrentSection.contents.findIndex((item) => item.code === itemId);
      const newIndex = newCurrentSection.contents.findIndex((item) => item.code === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        const contentsCopy = [...newCurrentSection.contents];
        const [movedItem] = contentsCopy.splice(oldIndex, 1);
        contentsCopy.splice(newIndex, 0, movedItem);
        newCurrentSection.contents = contentsCopy;
      }
    }

    return sections;
  };

  /**
   * Checks if an item is assigned to any section in the hierarchy
   * Used to determine if an item should be added to the unassigned items list
   *
   * @param {ISection[]} sections - The sections to search through
   * @param {string} itemCode - The item code to check for
   * @returns {boolean} True if the item is assigned to any section, false otherwise
   */
  const isItemAssignedToSection = (sections: ISection[], itemCode: string): boolean => {
    for (const section of sections) {
      if (section.contents.some((i) => i.code === itemCode)) {
        return true;
      }
      if (section.sections?.length && isItemAssignedToSection(section.sections, itemCode)) {
        return true;
      }
    }
    return false;
  };

  /**
   * Handles removing an item from the current section
   * If the item is not assigned elsewhere, it will be added to the unassigned items list
   *
   * @param {string} itemCode - The code of the item to remove
   */
  const handleRemoveItem = (itemCode: string) => {
    if (!selectedSectionId) return;
    //  Perform computations outside setState
    const newSections = sections;
    const targetSection = findSectionById(newSections, selectedSectionId);

    //  Exit early if section not found
    if (!targetSection) return;

    //  Remove item from secition contents
    targetSection.contents = targetSection.contents.filter((i: IContent) => i.code !== itemCode);

    //Check if item is assigned elsewhere
    const isAssignedElsewhere = isItemAssignedToSection(
      newSections.filter((s: ISection) => s.id !== selectedSectionId),
      itemCode
    );

    // Update sections state (simple state update, no logic)
    setSections(newSections);
    dispatch(updateCategoryModify());

    // Update unassigned items list if needed (separate state update)
    if (!isAssignedElsewhere) {
      const feature = categoryState.featureFamily.find((f) => f.code === itemCode);
      if (feature) {
        setGlobalUnassignedItems((prevItems) => [...prevItems, feature]);
      }
    }
  };

  /**
   * Saves the current category structure to the backend
   * Validates that all sections have valid arrays for contents and subsections
   * Updates the Redux store and exits edit mode on successful save
   */
  const handleSave = () => {
    const updatedData = getUpdatedCategoryData(product.id, sections);

    const isValid = updatedData.sections.every(
      (section) => Array.isArray(section.contents) && Array.isArray(section.sections)
    );

    if (!isValid) {
      return;
    }
    // Update the master view in Redux
    dispatch(saveMaster(updatedData));

    // Reset the modification flag
    setEditDataView(false);
  };

  /**
   * Handles the addition of a new category section or subcategory
   * Updates the master view and shows the add category dialog
   *
   * @param {ISection | null} parent - The parent section to add a subcategory to, or null to add a root category
   */
  const handleAddSection = (parent: ISection | null = null) => {
    const updatedData = getUpdatedCategoryData(product.id, sections);
    dispatch(updateMasterView(updatedData));

    if (parent) {
      // Add subcategory to an existing parent
      const section: ISection = {
        ...parent,
        // hideGeneral will be true for level 2+ categories (subcategories)
        hideGeneral: parent.hideGeneral || false,
        // Use 'Edit' for MultiNumeric categories, 'Add' for General categories
        action: parent.type === ECategoryType.MultiNumeric ? EActionType.Edit : EActionType.Add,
        // Pass through the type from parent if it's MultiNumeric, otherwise undefined to allow choice
        type: parent.type === ECategoryType.MultiNumeric ? ECategoryType.MultiNumeric : undefined,
      };
      dispatch(showAddCategoryDialog({ section, parent }));
    } else {
      // Adding a root level category
      dispatch(showAddCategoryDialog({ parent: null }));
    }
  };

  /**
   * Recursively removes a section and its references from the sections array
   * Preserves the structure of other sections and their subsections
   *
   * @param {ISection[]} sections - The array of sections to filter
   * @param {string} idToDelete - The ID of the section to remove
   * @returns {ISection[]} A new array with the specified section removed
   */
  const deleteRecursive = (sections: ISection[], idToDelete: string): ISection[] => {
    return sections
      .map((section) => ({
        ...section,
        sections: section.sections ? deleteRecursive(section.sections, idToDelete) : [],
      }))
      .filter((section) => section.id !== idToDelete);
  };

  /**
   * Initiates the deletion process for a category section
   * Sets up the confirmation dialog to confirm deletion
   *
   * @param {ISection} section - The section to be deleted
   */
  const handleDeleteSection = (section: ISection) => {
    setSectionToDelete(section);
    setConfirmDelete({
      open: true,
      title: 'messages.category.confirm',
      description: 'messages.category.confirmDelete',
      alertFor: EConfirmDialog.DeleteSection,
    });
  };

  const calculateUnAssigned = (sectionsToProcess: ISection[]) => {
    setSections(sectionsToProcess);
    // Calculate unassigned items
    const allAssignedCodes = new Set<string>();
    const processSection = (section: ISection) => {
      for (const content of section.contents || []) {
        allAssignedCodes.add(content.code);
      }
      for (const subSection of section.sections || []) {
        processSection(subSection);
      }
    };
    for (const section of sectionsToProcess) {
      processSection(section);
    }

    const unassignedItems = categoryState.featureFamily.filter(
      (feature: IFeatureFamily) => !allAssignedCodes.has(feature.code)
    );
    setGlobalUnassignedItems(unassignedItems);
  };

  /**
   * Handles user confirmation actions from dialog prompts
   * Processes section deletion or discards changes based on the confirmation type
   *
   * @param {Object} confirm - The confirmation object
   * @param {boolean} confirm.status - Whether the user confirmed the action
   * @param {string} confirm.alertFor - The type of confirmation dialog (e.g., DeleteSection, DiscardChanges)
   */
  const onConfirmAction = (confirm: { status: boolean; alertFor: string }) => {
    if (confirm.status && confirm.alertFor === EConfirmDialog.DeleteSection && sectionToDelete) {
      // Use recursive deletion - perform computation outside state update
      const updatedSections = deleteRecursive(sections, sectionToDelete.id);

      // Helper function to find next available section
      const findFirstAvailable = (sections: ISection[]): string | null => {
        for (const section of sections) {
          if (section.id !== sectionToDelete.id) {
            return section.id;
          }
          // Check in subsections
          if (section.sections?.length) {
            const found = findFirstAvailable(section.sections);
            if (found) return found;
          }
        }
        return null;
      };

      // Determine new selected section if current one is being deleted
      let newSelectedSectionId = selectedSectionId;
      if (selectedSectionId === sectionToDelete.id) {
        newSelectedSectionId = findFirstAvailable(updatedSections);
      }
      setSelectedSectionId(newSelectedSectionId);
      calculateUnAssigned(updatedSections);
    } else if (confirm.status && confirm.alertFor === EConfirmDialog.DiscardChanges) {
      // Handle discard changes confirmation
      onCancelClearChanges();
    }
    setConfirmDelete(initDialog);
    setSectionToDelete(null);
  };

  const enableEditView = () => {
    setEditDataView(true);
  };

  /**
   * Handles the cancellation of category editing
   * Prompts for confirmation if changes have been made, otherwise exits edit mode directly
   */
  const cancelView = () => {
    setConfirmDelete({
      open: true,
      title: 'messages.category.unsavedChanges',
      description: 'messages.category.discardChanges',
      alertFor: EConfirmDialog.DiscardChanges,
    });
    // }
  };

  /**
   * Clears all changes made during editing and reloads data from the server
   * Resets the changes flag and exits edit mode
   */
  const onCancelClearChanges = () => {
    // Reload data from Redux state
    if (!product?.id) return;

    const masterPayload = { productCode: product.id };
    dispatch(getMasterView(masterPayload));

    const featurePayload = {
      productCode: product.id,
      includeScopeFeatures: false,
      workItemNum: '',
      page: 0,
      limit: 0,
    };
    dispatch(getFeatureFamilyList(featurePayload));
    setEditDataView(false);
  };

  // Recursive function to render the category tree with drag-and-drop and selection highlight
  const renderDroppableTree = (
    nodes: ISection[],
    selectedId: string | null,
    parentExpanded: boolean = false
  ) => {
    return nodes.map((node) => {
      const hasSubcategories = node.sections && node.sections.length > 0;
      // Only highlight subcategories if their parent is expanded AND the parent is in the expanded array
      const isExpanded = expanded.includes(node.id);
      // If this node is selected, treat it as the only expanded parent for highlight
      const highlightChildren = isExpanded && node.id === selectedId;
      return (
        <TreeItem
          key={node.id}
          itemId={node.id}
          label={
            <div>
              <SortableCategory
                section={node}
                isSelected={node.id === selectedId}
                selectedSectionId={selectedId ?? undefined}
                onSelect={(sectionId: string) => setSelectedSectionId(sectionId)}
                onAddSection={handleAddSection}
                onDeleteSection={handleDeleteSection}
                isParentSelected={parentExpanded}
                editView={editDataView}
              />
            </div>
          }
        >
          {hasSubcategories
            ? renderDroppableTree(node.sections, selectedId, highlightChildren)
            : null}
        </TreeItem>
      );
    });
  };

  return (
    <div className="category">
      <div className="header-section">
        <div className="section-view">
          <h4>{t('labels.categories')}</h4>

          <div className="edit-view">
            <div className="icon-with-label">
              <IconType
                type="cancel"
                onClick={cancelView}
                aria-label="Cancel View"
                style={{
                  color: '#1474a4',
                  cursor: editDataView ? 'pointer' : 'not-allowed',
                  marginRight: 12,
                  opacity: editDataView ? 1 : 0.5,
                }}
                disabled={!editDataView}
                fontSize="large"
                className="header-large-icon"
              />
              <div className="icon-helper-text">Cancel</div>
            </div>
            <div className="icon-with-label">
              <IconType
                type="save"
                onClick={handleSave}
                aria-label="Save View"
                style={{
                  color: '#1474a4',
                  cursor: editDataView ? 'pointer' : 'not-allowed',
                  marginRight: 12,
                  opacity: editDataView ? 1 : 0.5,
                }}
                disabled={!editDataView}
                fontSize="large"
                className="header-large-icon"
              />
              <div className="icon-helper-text">Save</div>
            </div>
            {!editDataView && (
              <div className="icon-with-label">
                <IconType
                  type="edit"
                  onClick={enableEditView}
                  aria-label="Edit View"
                  style={{ color: '#1474a4', cursor: 'pointer', opacity: 1 }}
                  fontSize="large"
                  className="header-large-icon"
                />
                <div className="icon-helper-text">Edit</div>
              </div>
            )}
          </div>
        </div>
        <div className="category-header-section">
          <div className="breacrum-header-section">
            <Button
              variant="contained"
              disabled={!editDataView}
              endIcon={<AddIcon />}
              onClick={() => handleAddSection()}
              aria-label="Add category"
            >
              {t('button.add')}
            </Button>
            {breadCrumb && (
              <BreadCrumbComponent
                breadcrumb={breadCrumb}
                navigateToCategory={navigateToCategory}
              />
            )}
          </div>
        </div>
      </div>{' '}
      <DndContext
        sensors={sensors}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
        onDragStart={handleDragStart}
        collisionDetection={pointerWithin}
      >
        <div className="category-content">
          {/* Left pane start */}
          <section ref={setPaneRef(0)} className="left-pane pane resize-hor">
            <div className="categories-list">
              <SimpleTreeView
                selectedItems={selectedSectionId}
                expandedItems={expanded}
                onExpandedItemsChange={(_event, nodeIds) => setExpanded(nodeIds)}
                onSelectedItemsChange={(
                  _event: React.SyntheticEvent | null,
                  selectedIds: string | null
                ) => {
                  const id = Array.isArray(selectedIds) ? selectedIds[0] : selectedIds;
                  setSelectedSectionId(id);
                  // Toggle expand/collapse on label click
                  if (id && expanded.includes(id)) {
                    setExpanded(expanded.filter((eid) => eid !== id));
                  } else if (id) {
                    setExpanded([...expanded, id]);
                  }
                }}
              >
                {renderDroppableTree(sections, selectedSectionId)}
              </SimpleTreeView>
            </div>
          </section>
          <SplitBar index={0} paneRefs={{ current: paneRefs.current }} onResize={resizeHandler} />
          {/* Left pane end */}
          {/* Middle pane start */}{' '}
          <section
            ref={setPaneRef(1)}
            className={
              editDataView
                ? 'resize-hor middle-pane pane'
                : 'resize-hor middle-pane disabled-view pane'
            }
          >
            <div className="assigned-section-header">{t('labels.assigned')}</div>
            <DroppableZone
              id={EDropCategory.Assigned}
              items={selectedSection?.contents || []}
              isSortable={true}
              onRemove={handleRemoveItem}
              itemType={selectedSection?.type}
              isValidDragItem={isValidDragItem}
              overId={overId === EDropCategory.Assigned}
              containerType={EDropCategory.Assigned}
              isEditMode={editDataView}
            />
          </section>
          <SplitBar index={1} paneRefs={{ current: paneRefs.current }} onResize={resizeHandler} />
          {/* Middle pane end */}
          {/* Right pane start */}{' '}
          <section
            ref={setPaneRef(2)}
            className={
              editDataView
                ? 'right-pane resize-hor pane'
                : 'resize-hor right-pane disabled-view pane'
            }
          >
            <div className="unassingned-section-header">{t('labels.unassigned')}</div>
            <DroppableZone
              id={EDropCategory.Unassigned}
              items={globalUnassignedItems}
              isSortable={false}
              overId={overId === EDropCategory.Unassigned}
              containerType={EDropCategory.Unassigned}
              isEditMode={editDataView}
            />
          </section>
          {/* Right pane end */}
        </div>
        <DragOverlay>
          {activeId ? (
            <div className="drag-overlay-content">
              {active?.data?.current?.type === EDropCategory.Category ? (
                <>
                  <IconType type={active?.data?.current?.itemType} />
                  <span className="category-name">
                    {active?.data?.current?.itemName || activeId}
                  </span>
                  {showForbidden && (
                    <BlockIcon style={{ color: '#d32f2f', marginLeft: 8 }} fontSize="small" />
                  )}
                </>
              ) : (
                <>
                  <IconType type={active?.data?.current?.itemType} />
                  <div className="item-name">{active?.data?.current?.itemName || ''}</div>
                  {active?.data?.current?.itemCode && (
                    <div className="item-code">({active.data.current.itemCode})</div>
                  )}
                </>
              )}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
      {categoryState.showAddCategoryDialog && (
        <AddCategoryComponent open={categoryState.showAddCategoryDialog} />
      )}
      {confirmDelete.open && (
        <ConfirmationDialog dialogInfo={confirmDelete} onConfirmAction={onConfirmAction} />
      )}
    </div>
  );
};

export default Category;
